import React, { Children } from 'react';
import { SEO } from '../components/SEO';
export function PrivacyPage() {
  return <>
      <SEO title="Aspect — Privacy Policy" description="Privacy Policy explaining how Aspect collects, uses, stores, and protects personal information." data-id="element-591" />
      <div className="space-y-24 md:space-y-32" data-id="element-592">
        {/* Header */}
        <section className="max-w-2xl" data-id="element-593">
          <h1 className="text-3xl md:text-4xl font-medium leading-tight mb-4 text-aspect-black" data-id="element-594">
            Aspect Privacy Policy
          </h1>
          <p className="text-aspect-gray" data-id="element-595">Last updated: December 1, 2025</p>
          <div className="mt-8 space-y-4 text-aspect-black/80 leading-relaxed" data-id="element-596">
            <p data-id="element-597">
              Aspect ("Aspect," "we," "our," or "us") operates a platform that
              documents completed architectural projects and enables the
              licensing of verified, rights-cleared imagery. This Privacy Policy
              explains how we collect, use, store, and protect personal
              information in connection with our website and platform (the
              "Services").
            </p>
            <p data-id="element-598">
              We design our systems to minimize unnecessary data collection and
              to handle personal information with care, transparency, and
              purpose.
            </p>
          </div>
        </section>

        {/* 1. Scope */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-599">
          <div className="md:col-span-4" data-id="element-600">
            <span className="text-aspect-gray" data-id="element-601">01</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-602">
            <h2 className="text-2xl font-medium" data-id="element-603">Scope</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-604">
              <p data-id="element-605">
                This Privacy Policy applies to information collected through:
              </p>
              <ul className="space-y-2" data-id="element-606">
                <li data-id="element-607">The Aspect website</li>
                <li data-id="element-608">Project submission and intake workflows</li>
                <li data-id="element-609">Photographer onboarding and archive submission</li>
                <li data-id="element-610">Manufacturer account creation and licensing</li>
                <li data-id="element-611">
                  Communications with Aspect (email, forms, or direct contact)
                </li>
              </ul>
              <p data-id="element-612">
                It does not apply to third-party websites or services that may
                be linked from our site.
              </p>
            </div>
          </div>
        </section>

        {/* 2. Information We Collect */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-613">
          <div className="md:col-span-4" data-id="element-614">
            <span className="text-aspect-gray" data-id="element-615">02</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-616">
            <h2 className="text-2xl font-medium" data-id="element-617">Information We Collect</h2>
            <div className="space-y-6 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-618">
              <p data-id="element-619">
                We collect only information necessary to operate the platform,
                coordinate photography, clear rights, and deliver licensed
                assets.
              </p>

              <div data-id="element-620">
                <h3 className="font-medium mb-2" data-id="element-621">
                  a. Information You Provide Directly
                </h3>
                <p data-id="element-622">Depending on your role, this may include:</p>
                <ul className="space-y-2 mt-2" data-id="element-623">
                  <li data-id="element-624">
                    Contact information (name, email address, company, role)
                  </li>
                  <li data-id="element-625">
                    Project information (project name, general location,
                    building type)
                  </li>
                  <li data-id="element-626">
                    Professional information (architect firm, manufacturer
                    brand, photographer portfolio)
                  </li>
                  <li data-id="element-627">
                    Rights and release information (architect, owner, and
                    photographer confirmations)
                  </li>
                  <li data-id="element-628">
                    Payment and billing details (for manufacturers and
                    photographers, processed via third-party providers)
                  </li>
                </ul>
                <p className="mt-4" data-id="element-629">
                  We do not request sensitive personal data unless it is
                  required for rights verification or contractual compliance.
                </p>
              </div>

              <div data-id="element-630">
                <h3 className="font-medium mb-2" data-id="element-631">
                  b. Information Collected Automatically
                </h3>
                <p data-id="element-632">
                  When you use our website or platform, we may collect limited
                  technical information, such as:
                </p>
                <ul className="space-y-2 mt-2" data-id="element-633">
                  <li data-id="element-634">IP address</li>
                  <li data-id="element-635">Browser type and device information</li>
                  <li data-id="element-636">Pages visited and actions taken</li>
                  <li data-id="element-637">Date and time of access</li>
                </ul>
                <p className="mt-4" data-id="element-638">
                  This information is used for security, reliability, and basic
                  analytics. We do not use behavioral advertising trackers.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 3. How We Use Information */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-639">
          <div className="md:col-span-4" data-id="element-640">
            <span className="text-aspect-gray" data-id="element-641">03</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-642">
            <h2 className="text-2xl font-medium" data-id="element-643">How We Use Information</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-644">
              <p data-id="element-645">
                We use collected information solely to operate and improve the
                Services, including to:
              </p>
              <ul className="space-y-2" data-id="element-646">
                <li data-id="element-647">Coordinate and manage photography projects</li>
                <li data-id="element-648">Verify products, projects, and usage rights</li>
                <li data-id="element-649">Deliver preview galleries and approvals</li>
                <li data-id="element-650">Enable image licensing and downloads</li>
                <li data-id="element-651">Process payments and compensation</li>
                <li data-id="element-652">Communicate operational updates and account information</li>
                <li data-id="element-653">Maintain platform security and integrity</li>
              </ul>
              <p data-id="element-654">
                We do not sell personal information or use it for unrelated
                marketing purposes.
              </p>
            </div>
          </div>
        </section>

        {/* 4. Use of Images */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-655">
          <div className="md:col-span-4" data-id="element-656">
            <span className="text-aspect-gray" data-id="element-657">04</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-658">
            <h2 className="text-2xl font-medium" data-id="element-659">
              Use of Images and Project Data
            </h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-660">
              <p data-id="element-661">
                Aspect documents real, completed projects with the consent of
                participating parties.
              </p>
              <ul className="space-y-2" data-id="element-662">
                <li data-id="element-663">
                  Images are published and licensed only after required releases
                  and approvals are secured.
                </li>
                <li data-id="element-664">
                  Owners and architects may control visibility, attribution, and
                  location specificity.
                </li>
                <li data-id="element-665">
                  Any incidental personal information captured in imagery (e.g.,
                  people, identifying details) is removed, blurred, or excluded
                  before publication.
                </li>
                <li data-id="element-666">
                  Approved images may be licensed to manufacturers under
                  explicit commercial usage terms.
                </li>
              </ul>
              <p data-id="element-667">
                Project metadata is treated as contextual information, not
                personal profiling data.
              </p>
            </div>
          </div>
        </section>

        {/* 5. Sharing */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-668">
          <div className="md:col-span-4" data-id="element-669">
            <span className="text-aspect-gray" data-id="element-670">05</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-671">
            <h2 className="text-2xl font-medium" data-id="element-672">Sharing of Information</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-673">
              <p data-id="element-674">
                We share information only as necessary to operate the platform:
              </p>
              <ul className="space-y-2" data-id="element-675">
                <li data-id="element-676">
                  With participating parties (e.g., architects, owners,
                  photographers) to coordinate shoots and approvals
                </li>
                <li data-id="element-677">
                  With manufacturers when licensing images, limited to what is
                  required for attribution and rights clarity
                </li>
                <li data-id="element-678">
                  With service providers (e.g., hosting, payment processing)
                  under confidentiality obligations
                </li>
              </ul>
              <p data-id="element-679">
                We do not share personal information with third parties for
                advertising or data brokerage purposes.
              </p>
            </div>
          </div>
        </section>

        {/* 6. Data Retention */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-680">
          <div className="md:col-span-4" data-id="element-681">
            <span className="text-aspect-gray" data-id="element-682">06</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-683">
            <h2 className="text-2xl font-medium" data-id="element-684">Data Retention</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-685">
              <p data-id="element-686">We retain information only as long as necessary to:</p>
              <ul className="space-y-2" data-id="element-687">
                <li data-id="element-688">Maintain rights records and audit trails</li>
                <li data-id="element-689">Support licensed asset usage</li>
                <li data-id="element-690">
                  Comply with legal, contractual, or regulatory obligations
                </li>
              </ul>
              <p data-id="element-691">
                When information is no longer required, it is securely deleted
                or anonymized.
              </p>
            </div>
          </div>
        </section>

        {/* 7. Your Rights */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-692">
          <div className="md:col-span-4" data-id="element-693">
            <span className="text-aspect-gray" data-id="element-694">07</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-695">
            <h2 className="text-2xl font-medium" data-id="element-696">Your Rights and Choices</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-697">
              <p data-id="element-698">Depending on your jurisdiction, you may have the right to:</p>
              <ul className="space-y-2" data-id="element-699">
                <li data-id="element-700">
                  Request access to personal information we hold about you
                </li>
                <li data-id="element-701">
                  Request correction or deletion of inaccurate information
                </li>
                <li data-id="element-702">Withdraw consent where applicable</li>
                <li data-id="element-703">
                  Request removal of specific images or project references
                </li>
              </ul>
              <p data-id="element-704">
                Requests can be made by contacting us at the email address
                below. We respond promptly and transparently.
              </p>
            </div>
          </div>
        </section>

        {/* 8. Security */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-705">
          <div className="md:col-span-4" data-id="element-706">
            <span className="text-aspect-gray" data-id="element-707">08</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-708">
            <h2 className="text-2xl font-medium" data-id="element-709">Security</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-710">
              <p data-id="element-711">
                We use reasonable administrative, technical, and organizational
                safeguards to protect information from unauthorized access,
                disclosure, or misuse.
              </p>
              <p data-id="element-712">
                No system is perfectly secure, but Aspect is designed to
                prioritize reliability, traceability, and control.
              </p>
            </div>
          </div>
        </section>

        {/* 9. Children's Privacy */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-713">
          <div className="md:col-span-4" data-id="element-714">
            <span className="text-aspect-gray" data-id="element-715">09</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-716">
            <h2 className="text-2xl font-medium" data-id="element-717">Children's Privacy</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-718">
              <p data-id="element-719">
                Aspect is not intended for use by children, and we do not
                knowingly collect personal information from individuals under 13
                years of age.
              </p>
            </div>
          </div>
        </section>

        {/* 10. Changes */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-720">
          <div className="md:col-span-4" data-id="element-721">
            <span className="text-aspect-gray" data-id="element-722">10</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-723">
            <h2 className="text-2xl font-medium" data-id="element-724">Changes to This Policy</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-725">
              <p data-id="element-726">
                We may update this Privacy Policy as the platform evolves.
                Material changes will be reflected by updating the "Last
                updated" date and, where appropriate, providing notice through
                the Services.
              </p>
            </div>
          </div>
        </section>

        {/* 11. Contact */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12 pb-12" data-id="element-727">
          <div className="md:col-span-4" data-id="element-728">
            <span className="text-aspect-gray" data-id="element-729">11</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-730">
            <h2 className="text-2xl font-medium" data-id="element-731">Contact</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-732">
              <p data-id="element-733">For privacy questions or requests, contact:</p>
              <p className="font-medium" data-id="element-734">Aspect</p>
              <p data-id="element-735">Email: privacy@aspectstudio.io</p>
            </div>
          </div>
        </section>
      </div>
    </>;
}